import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * DS TextInput — figma 257:1538 "textplace-16" (default) / 357:9703 (error state).
 *
 * 시각 (default): white bg, border 1px Border/Default, radius 6, padding 14×10
 * 텍스트: Pretendard Regular 14/22 Text/Primary (placeholder: Text/Tertiary)
 *
 * error: 비어있지 않은 문자열이면 border System/Error + 하단 12/18 System/Error 메시지
 * multiline=true 면 textarea (figma 266:1337 — 100h)
 * readOnly=true 면 BG/Paper (figma 275:102 외부연동 readonly variant)
 * suffix 가 있으면 우측 안쪽에 단위 텍스트 (figma 4081:131109 "0 개" — SemiBold 14/22 Text/Tertiary).
 *   단일행 input 전용(multiline 무시). suffix 폭만큼 input padding-right 를 늘린다.
 *
 * @param {object} props
 * @param {string} props.value
 * @param {(v: string) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {boolean} [props.multiline]
 * @param {number} [props.rows=4]
 * @param {boolean} [props.readOnly]
 * @param {string} [props.type="text"]
 * @param {string} [props.error] - 에러 메시지 (truthy면 에러 상태)
 * @param {React.ReactNode} [props.suffix] - 우측 단위 텍스트 ("개", "원" 등). style 의 폭(width/minWidth/maxWidth/flex*)은 래퍼에도 적용된다
 */
const SIZE_STYLE_KEYS = ["width", "minWidth", "maxWidth", "flex", "flexBasis", "flexGrow", "flexShrink"];

const DSTextInput = ({
  value,
  onChange,
  placeholder,
  multiline = false,
  rows = 4,
  readOnly = false,
  type = "text",
  error,
  suffix,
  className = "",
  style,
  ...rest
}) => {
  const hasError = !!error;
  const useSuffix = !multiline && suffix !== undefined && suffix !== null && suffix !== "";
  const suffixRef = useRef(null);
  const [suffixPad, setSuffixPad] = useState(0);
  // suffix 폭 + 좌우 여백(14 + 8) 만큼 input 우측 padding 확보
  useLayoutEffect(() => {
    if (!useSuffix || !suffixRef.current) {
      setSuffixPad(0);
      return;
    }
    setSuffixPad(suffixRef.current.offsetWidth + 14 + 8);
  }, [useSuffix, suffix]);
  const inputCls = [
    "ds-text-input",
    readOnly && "ds-text-input--readonly",
    hasError && "ds-text-input--error",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const inputEl = multiline ? (
    <textarea
      className={inputCls}
      rows={rows}
      value={value ?? ""}
      placeholder={placeholder}
      readOnly={readOnly}
      onChange={(e) => onChange && onChange(e.target.value)}
      style={style}
      {...rest}
    />
  ) : (
    <input
      className={inputCls}
      type={type}
      value={value ?? ""}
      placeholder={placeholder}
      readOnly={readOnly}
      onChange={(e) => onChange && onChange(e.target.value)}
      // type=number 에서 마우스 휠로 값이 바뀌는 브라우저 기본 동작 차단 (포커스 해제)
      onWheel={type === "number" ? (e) => e.currentTarget.blur() : undefined}
      style={useSuffix && suffixPad ? { ...style, paddingRight: suffixPad } : style}
      {...rest}
    />
  );

  // suffix 가 있을 때만 relative 래퍼를 둔다 — 없으면 input 단독 DOM.
  // 래퍼가 부모 레이아웃의 flex item 이 되므로 style 의 폭 관련 값은 래퍼에도 준다
  // (안 주면 래퍼가 width:100% 로 늘어나 suffix 가 input 에서 떨어진다)
  const fieldStyle =
    useSuffix && style
      ? SIZE_STYLE_KEYS.reduce((acc, key) => {
          if (style[key] !== undefined) acc[key] = style[key];
          return acc;
        }, {})
      : undefined;
  const fieldEl = useSuffix ? (
    <div className="ds-text-input-field" style={fieldStyle}>
      {inputEl}
      <span ref={suffixRef} className="ds-text-input-field__suffix" aria-hidden>
        {suffix}
      </span>
    </div>
  ) : (
    inputEl
  );

  // 에러 유무와 무관하게 항상 동일한 루트(div)를 렌더한다.
  // 에러 토글 시 루트 타입이 input↔div 로 바뀌면 React 가 input 을 remount 하여 입력 중
  // 포커스를 잃는 문제가 생긴다. wrapper 는 display:contents 라 레이아웃에 영향 없음.
  // (figma 357:9703 — error text 12/18 System/Error)
  return (
    <div className="ds-text-input-wrap">
      {fieldEl}
      {hasError && <div className="ds-text-input-wrap__error">{error}</div>}
    </div>
  );
};

export default React.memo(DSTextInput);
